// Outer
import * as Sentry from '@sentry/nextjs';
import React from 'react';
import * as yup from 'yup';
import { FormikProvider, useFormik } from 'formik';
import { Alert, Box, Button, FormGroup, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation } from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp, library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons';

// Inner
import FormikTextField from '@/common/components/forms/TextField';
import { useApiContext } from '@/common/contexts/ApiContext';
import { ApiError } from '@/common/api/types';

library.add(fas, far);

// Methods
function getInitialValues() {
  return {
    name: '',
    iconName: '',
  }
}

const validationSchema = yup.object({
  name: yup.string().required(),
  iconName: yup.string(),
});

function useKnowledeBaseCategoryForm(onClose: () => void, refetch: () => void) {
  const { enqueueSnackbar } = useSnackbar();
  const { apiClient } = useApiContext();

  const createKnowledgeBaseCategoryMutation = useMutation<
    any,
    ApiError | Error,
    any
  >(apiClient.knowledgebasecategories.create, {
    onSuccess: onSiteKnowledgeBaseCategorySuccess,
    onError,
  });

  const initialValues = getInitialValues();

  const formik = useFormik<any>({
    initialValues,
    validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  function onSiteKnowledgeBaseCategorySuccess() {
    enqueueSnackbar('Knowledge Base Category created', { variant: 'success' });
    refetch()

    formik.setSubmitting(false);
    onClose();
  }
  function onError(error: ApiError | Error) {
    enqueueSnackbar(error.message, { variant: 'error' });
    Sentry.captureException(error);
    formik.setSubmitting(false);
  }

  function onSubmit(values: any) {
    createKnowledgeBaseCategoryMutation.mutate(values);
  }

  return {
    formik,
  };
}

export default function CreateKnowledgeBaseCategoryForm({
  refetch,
  onClose,
}: any) {
  const { formik } = useKnowledeBaseCategoryForm(onClose, refetch);

  return (
    <FormikProvider value={formik}>
      <Box display="flex" flexDirection="column">
        <FormGroup>
          <Box marginTop={2}>
            <Typography>Create Knowledge Base Category</Typography>
          </Box>

          <Box marginTop={2}>
            <FormikTextField
              fullWidth
              id="name"
              name="name"
              type="text"
              label="Name"
              variant="outlined"
              tooltiptitle="Enter Knowledgebase Article Title."
            />
          </Box>

          <Box marginTop={2}>
            <Box marginTop={1} marginBottom={1}>
              <Typography variant="h6">Category Icon</Typography>
            </Box>
            <Box marginTop={1} marginBottom={2}>
              <Alert severity="warning">
                <Typography variant="body1">
                  You can set any{' '}
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href="https://fontawesome.com/v6.0/icons"
                  >
                    fontawesome
                  </a>{' '}
                  icon by its name.
                </Typography>
              </Alert>
            </Box>
            <Box marginBottom={2}>
              <FormikTextField
                fullWidth
                id="iconName"
                name="iconName"
                type="text"
                label="Icon Name"
                variant="outlined"
                tooltiptitle="Enter a FontAwesome icon name."
              />
            </Box>
            <Box marginTop={1} marginBottom={1}>
              <Typography variant="h6">Category Icon Preview</Typography>
            </Box>
            <FontAwesomeIcon
              size="3x"
              color='#fff'
              icon={formik.values.iconName as IconProp}
            />
          </Box>

        </FormGroup>

        <Box marginTop={1} marginBottom={1}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            disabled={false} // TODO: Add Logic
            onClick={formik.submitForm}
          >
            Save
          </Button>
        </Box>
      </Box>
    </FormikProvider>
  );
}
